import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CrosswalkProvider, useCrosswalk } from './context/CrosswalkContext';
import { AdminAssessmentProvider } from './context/AdminAssessmentContext';
import { NotificationProvider } from './context/NotificationContext';
import { setApiAuthToken } from './services/api';
import CrosswalkModal from './components/CrosswalkModal';
import Header from './components/Header';
import ReviewerHeader from './components/ReviewerHeader';
import UserHeader from './components/UserHeader';
import Dashboard from './pages/admin/Dashboard';
import Organizations from './pages/admin/Organizations';
import Users from './pages/Users';
import Assessments from './pages/admin/Assessments/Assessments';
import Questionnaires from './pages/admin/Questionnaires';
import Questions from './pages/admin/Questions';
import Settings from './pages/Settings';
import HelpSupport from './pages/HelpSupport';
import Profile from './pages/Profile';
import Activity from './pages/Activity';
// Reviewer pages
import ReviewerDashboard from './pages/reviewer/ReviewerDashboard';
import ReviewerAssessments from './pages/reviewer/ReviewerAssessments';
import ReviewerQuestionnaires from './pages/reviewer/ReviewerQuestionnaires';
// User pages
import UserDashboard from './pages/user/UserDashboard';
import UserAssessments from './pages/user/UserAssessments';
import UserQuestionnaires from './pages/user/UserQuestionnaires';
// Guest pages
import Guest from './pages/guest/Guest';
import { FiLoader, FiShield } from 'react-icons/fi';
import UserRequests from './pages/user/UserRequests';
import AdminRequests from './pages/admin/AdminRequests';
import Documents from './pages/admin/Documents/Documents';


// ── Protected route — shows loading spinner until auth token arrives from RiskWebApp ──
const ProtectedRoute = ({ children }) => {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <FiLoader className="animate-spin text-4xl text-[#0b79b8] mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Authenticating with Risk Web App…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <FiShield className="text-5xl text-red-400 mx-auto mb-4" />
          <p className="text-slate-700 font-semibold mb-2">Not Authenticated</p>
          <p className="text-slate-500 text-sm">Please log in through the Risk Assessment portal.</p>
        </div>
      </div>
    );
  }

  return children;
};


// Main Layout Component (Admin)
const MainLayout = ({ children }) => {
  const { user } = useAuth();

  return (
    // <div className="flex flex-col h-screen bg-slate-100">
    <div className="flex flex-col h-screen bg-[#ffffff]">
      <Header />
      <main className="flex-1 p-6 overflow-auto">
        {React.cloneElement(children, { user })}
      </main>
    </div>
  );
};

// Reviewer Layout Component
const ReviewerLayout = ({ children }) => {
  return (
    // <div className="flex flex-col h-screen bg-slate-100">
    <div className="flex flex-col h-screen bg-[#ffffff]">
      <ReviewerHeader />
      <main className="flex-1 p-6 overflow-auto">
        {children}
      </main>
    </div>
  );
};

// User Layout Component
const UserLayout = ({ children }) => {
  return (
    // <div className="flex flex-col h-screen bg-slate-100">
    <div className="flex flex-col h-screen bg-[#ffffff]">
      <UserHeader />
      <main className="flex-1 p-6 overflow-auto">
        {children}
      </main>
    </div>
  );
};

/**
 * TokenBridge — syncs the Keycloak token from AuthContext into the api.js
 * module-level token store so every apiFetch call includes the Bearer header.
 */
const TokenBridge = ({ children }) => {
  const { token } = useAuth();
  React.useEffect(() => {
    if (token) {
      setApiAuthToken(token);
      console.log('[TokenBridge] API auth token set');
    }
  }, [token]);
  return children;
};

function AppContent() {

  console.log("[AppContent] Rendering with auth state");
  const { role, loading, assessmentId } = useAuth();
  const { isOpen: isCrosswalkOpen, minimized: isCrosswalkMinimized, crosswalkJobs: cwJobs, closeCrosswalkModal, minimizeCrosswalk, restoreCrosswalk, markCrosswalkDone } = useCrosswalk();

  // Check if current path is /guest and return early to skip auth logic
  const currentPath = window.location.pathname;
  const fullUrl = window.location.href;

  // Enhanced debugging for guest route detection
  console.log("[AppContent] Current pathname:", currentPath);
  console.log("[AppContent] Full URL:", fullUrl);
  console.log("[AppContent] Checking for guest route...");

  // More robust guest route detection
  const isGuestRoute = currentPath.includes('/guest') ||
    currentPath.endsWith('/guest') ||
    fullUrl.includes('/trust-center/guest');

  console.log("[AppContent] Is guest route?", isGuestRoute);

  if (isGuestRoute) {
    console.log("[AppContent] ✅ Rendering Guest component");
    return (
      <Routes>
        <Route path="/trust-center/guest" element={<Guest />} />
        <Route path="*" element={<Guest />} />
      </Routes>
    );
  }

  const currentRole = role || 'trustcenteruser';

  console.log("[AppContent] Current role:", currentRole);
  console.log("[AppContent] Loading state:", loading);

  // Determine default redirect based on role and assessmentId
  const getDefaultRoute = () => {
    // Normalize assessmentId: treat null, undefined, '', '0', 0 all as "no assessmentId"
    const hasValidAssessmentId = assessmentId && String(assessmentId) !== '' && String(assessmentId) !== '0';
    
    const defaultRoute = (() => {


      console.log("[AppContent] Checking hasValidAssessmentId:", hasValidAssessmentId, "and current role:", currentRole);
      
      switch (currentRole) {
        case 'admin':
        case 'assessmentadmin':
        case 'departmentadmin':
        case 'department_admin':
          return hasValidAssessmentId 
            ? `/assessments` 
            : '/dashboard';
        case 'reviewer':
          return hasValidAssessmentId 
            ? `/reviewer/questionnaires/${assessmentId}` 
            : '/reviewer/dashboard';
        case 'trustcenteruser':
          return hasValidAssessmentId 
            ? `/user/questionnaires/${assessmentId}` 
            : '/user/dashboard';
       
        default:
          return '/dashboard';
      }
    })();
    console.log("[AppContent] Default route for role", currentRole, ":", defaultRoute, "(assessmentId:", assessmentId, ")");
    return defaultRoute;
  };

  // Show loading while waiting for auth
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <FiLoader className="animate-spin text-4xl text-[#0b79b8] mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Loading Trust Center…</p>
        </div>
      </div>
    );
  }

  return (
<>
      <Routes>
        {/* ═══════════════ GUEST ROUTE (PUBLIC - NO AUTH REQUIRED) ═══════════════ */}
        <Route path="/guest" element={<Guest />} />

        {/* ═══════════════ ADMIN ROUTES (existing — untouched) ═══════════════ */}
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MainLayout><Dashboard /></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/organizations" element={
          <ProtectedRoute>
            <MainLayout><Organizations /></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute>
            <MainLayout><Users /></MainLayout>
          </ProtectedRoute>
        } />
      
        <Route path="/assessments" element={
          <ProtectedRoute>
            <AdminAssessmentProvider>
              <MainLayout><Assessments /></MainLayout>
            </AdminAssessmentProvider>
          </ProtectedRoute>
        } />
        <Route path="/questionnaires" element={
          <ProtectedRoute>
            <MainLayout><Questionnaires /></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/questions" element={
          <ProtectedRoute>
            <MainLayout><Questions /></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <MainLayout><Settings /></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/help-support" element={
          <ProtectedRoute>
            <MainLayout><HelpSupport /></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <MainLayout><Profile /></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/activity" element={
          currentRole === 'admin' ? (
            <ProtectedRoute>
              <MainLayout><Activity /></MainLayout>
            </ProtectedRoute>
          ) : <Navigate to="/" replace />
        } />

         <Route path='/requests' element={
         <ProtectedRoute>
            <MainLayout>
            <AdminRequests />
            </MainLayout>
         </ProtectedRoute>
        } />

        <Route path='/documents' element={
          <ProtectedRoute>
            <MainLayout><Documents /></MainLayout>
          </ProtectedRoute>
        } />

        {/* ═══════════════ REVIEWER ROUTES ═══════════════ */}
        <Route path="/reviewer/dashboard" element={
          <ReviewerLayout><ReviewerDashboard /></ReviewerLayout>
        } />
        <Route path="/reviewer/assessments" element={
          <ReviewerLayout><ReviewerAssessments /></ReviewerLayout>
        } />
        <Route path="/reviewer/questionnaires/:assessmentId" element={
          <ReviewerLayout><ReviewerQuestionnaires /></ReviewerLayout>
        } />

        {/* ═══════════════ USER ROUTES ═══════════════ */}
        <Route path="/user/dashboard" element={
          <UserLayout><UserDashboard /></UserLayout>
        } />
        <Route path="/user/assessments" element={
          <UserLayout><UserAssessments /></UserLayout>
        } />
        <Route path="/user/questionnaires/:assessmentId" element={
          <UserLayout><UserQuestionnaires /></UserLayout>
        } />

          <Route path='/user/requests' element={
          <UserLayout>
            <UserRequests />
          </UserLayout>
        } />

        {/* ═══════════════ TRUST CENTER USER ROUTES ═══════════════ */}
        <Route path="/trustcenteruser/dashboard" element={
          <UserLayout><UserDashboard /></UserLayout>
        } />
        <Route path="/trustcenteruser/assessments" element={
          <UserLayout><UserAssessments /></UserLayout>
        } />
        <Route path="/trustcenteruser/questionnaires/:assessmentId" element={
          <UserLayout><UserQuestionnaires /></UserLayout>
        } />

        {/* Catch all - redirect based on role */}
        <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
      </Routes>

      {/* Global crosswalk modal — persists across page navigation */}
      <CrosswalkModal
        isOpen={isCrosswalkOpen}
        minimized={isCrosswalkMinimized}
        onClose={closeCrosswalkModal}
        onMinimize={minimizeCrosswalk}
        onRestore={restoreCrosswalk}
        onComplete={markCrosswalkDone}
        crosswalkJobs={cwJobs}
      />

      </>
   
  );
}

function App() {
  console.log("[App] Initial render");
  return (
    <Router>
      <AuthProvider>
        
        <NotificationProvider>
          <TokenBridge>
            <CrosswalkProvider>
              <AppContent />
            </CrosswalkProvider>
          </TokenBridge>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
